import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Image,
  Portal,
  chakra,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { MoreVertical } from 'components/atoms/Icons'
import Select from 'react-select'
import { CardLoading } from '../CardLoading'
import { DataMoreVerticalType } from '../SearchPost/hook'
import { ArticleSummaryReport } from './ArticleSummaryReport'
import { OptionCompany } from './hook'

const MotionBox = motion(Box)

const TextHidden = chakra('span', {
  baseStyle: {
    display: 'inline-block',
    textIndent: '-9999px',
    whiteSpace: 'pre',
  },
})

type PeekCarouselProps = {
  clearCache: boolean
  handleSetPagePdf: (e: string, type?: boolean) => void
  baseNewsId: string
  pdfPage?: string
  selectedCompanies: OptionCompany[]
  isDetailModalOpen: boolean
  onDetailModalOpen: () => void
  handleCopy: (id: string) => any
  dataMoreVertical: DataMoreVerticalType[]
  index: number
  setIndex: (i: number) => void
  isOpenDrawer: boolean
  report_type?: number
}

export const PeekCarousel: FC<PeekCarouselProps> = ({
  clearCache,
  handleSetPagePdf,
  baseNewsId,
  pdfPage,
  selectedCompanies,
  isDetailModalOpen,
  onDetailModalOpen,
  handleCopy,
  dataMoreVertical,
  index,
  setIndex,
  isOpenDrawer,
  report_type,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const [SLIDE_W, setSlideW] = useState(0)
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>(
    () => {
      const initial: Record<number, boolean> = {}
      selectedCompanies.forEach(s => {
        if (s.news_id) {
          initial[s.news_id] = true
        }
      })
      return initial
    },
  )
  const GAP = 0

  useLayoutEffect(() => {
    function measure() {
      if (!containerRef.current) return
      setSlideW(containerRef.current.clientWidth)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [isOpenDrawer])

  useEffect(() => {
    setLoadingStates(prev => {
      const updated = { ...prev }
      selectedCompanies.forEach(s => {
        if (s.news_id && !(s.news_id in updated)) {
          updated[s.news_id] = true
        }
      })
      return updated
    })
  }, [selectedCompanies])

  const slot = SLIDE_W + GAP

  const translateX = -index * slot

  const handleLoadingChange = (
    newsId: number | undefined,
    loading: boolean,
  ) => {
    if (newsId) {
      setLoadingStates(prev => ({ ...prev, [newsId]: loading }))
    }
  }

  return (
    <Box>
      <Flex alignItems="center" height={7} mb="8px">
        <Select
          closeMenuOnSelect={false}
          filterOption={(option, inputValue) =>
            option.data.company_name
              .toLowerCase()
              .includes(inputValue.toLowerCase())
          }
          formatOptionLabel={(opt: OptionCompany) => (
            <Text fontSize="14px">
              {opt.company_name}
              {report_type === 4 && opt.report_year
                ? ` (${opt.report_year})`
                : ''}
            </Text>
          )}
          menuIsOpen={open}
          menuPortalTarget={document.body}
          options={selectedCompanies}
          styles={{
            control: base => ({
              ...base,
              borderRadius: '12px',
              borderColor: '#E8ECEF',
              width: '250px',
              height: '40px',
              boxShadow: '0px 5px 10px -5px #1417181A inset',
              backgroundColor: '#FEFEFE',
              '&:hover': {
                borderColor: '#0084FF',
                cursor: 'pointer',
              },
              paddingLeft: '8px',
            }),
            placeholder: base => ({
              ...base,
              opacity: 0.3,
            }),
            input: base => ({
              ...base,
              textAlign: 'left',
            }),
            valueContainer: base => ({
              ...base,
              padding: '0 !important',
              margin: 0,
            }),
            menu: base => ({
              ...base,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '0',
              position: 'absolute',
            }),
            menuPortal: base => ({
              ...base,
              zIndex: 9999,
              pointerEvents: 'auto',
            }),
            option: (base, state) => ({
              ...base,
              cursor: state.isDisabled ? 'not-allowed' : 'pointer',
              backgroundColor: state.isSelected ? '#FEFEFE' : '#FFF',
              color: state.isDisabled ? '#999' : '#000',
              '&:hover': {
                backgroundColor: state.isDisabled ? '#FFF' : '#E6F2FF',
              },
            }),
            indicatorsContainer: base => ({
              ...base,
              color: '#3182ce',
              svg: { fill: '#3182ce' },
            }),
            dropdownIndicator: base => ({
              ...base,
              color: '#3182ce',
              '&:hover': { color: '#2B6CB0' },
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
            clearIndicator: base => ({
              ...base,
              color: '#3182ce',
              cursor: 'pointer',
              '&:hover': { color: '#2B6CB0' },
            }),
          }}
          value={selectedCompanies[index]}
          onChange={opt => {
            setIndex(
              selectedCompanies.findIndex(c => c.news_id === opt?.news_id),
            )
            setOpen(false)
          }}
          onMenuClose={() => setOpen(false)}
          onMenuOpen={() => setOpen(true)}
        />
      </Flex>
      <Box maxW="100%" position="relative" pt={4}>
        <Box
          ref={containerRef}
          borderRadius="12px"
          maxWidth="100%"
          overflow="hidden"
          width="100%"
        >
          <MotionBox
            animate={{ x: translateX }}
            display="flex"
            transition={{ type: 'spring', stiffness: 100, damping: 22 }}
            width={`100%`}
          >
            {selectedCompanies.map((s, i) => {
              const isCenter = i === index
              const isLoading = loadingStates[s.news_id ?? 0] ?? false
              return (
                <Box
                  key={s.news_id}
                  bgColor="gray.600"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="12px"
                  boxShadow={isCenter ? 'lg' : 'sm'}
                  flex={`0 0 100%`}
                  id={s.news_id?.toString()}
                  mr={`${GAP}px`}
                  opacity={isCenter ? 1 : 0.85}
                  pt={2}
                  px={2}
                  transform={isCenter ? 'scale(1)' : 'scale(0.96)'}
                  transition="transform 0.25s, box-shadow 0.25s"
                >
                  <TextHidden position={'absolute'}>
                    {s.company_name}
                    {report_type === 4 && s.report_year
                      ? ` (${s.report_year})`
                      : ''}
                  </TextHidden>
                  <Flex pb={2} px={1}>
                    <Text fontSize="16px" fontWeight="700">
                      <b>{s.news_title}</b>
                    </Text>
                    <Menu>
                      <MenuButton mb="5px" ml="auto">
                        <MoreVertical
                          color="gray.800"
                          fontSize="2xl"
                          opacity={0.3}
                        />
                      </MenuButton>
                      <Portal>
                        <MenuList
                          bgColor="black.300"
                          borderRadius="20px"
                          display="flex"
                          flexDirection="column"
                          gap="16px"
                          maxH="194px"
                          maxW="337px"
                          overflow="auto"
                          overflowY={'auto'}
                          padding="24px"
                          zIndex={2}
                        >
                          {dataMoreVertical.map((item, index) => (
                            <MenuItem
                              key={index}
                              bgColor="transparent"
                              gap="16px"
                              padding={0}
                              onClick={() => {
                                if (item.title === '全画面表示') {
                                  onDetailModalOpen()
                                } else {
                                  handleCopy(s.news_id?.toString() ?? '0')
                                }
                              }}
                            >
                              <Flex align="center" gap="16px">
                                <Image src={item.icon} />
                                <Text
                                  color="white.50"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  letterSpacing="-0.02em"
                                  lineHeight="shorter"
                                >
                                  {item.title}
                                </Text>
                              </Flex>
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Portal>
                    </Menu>
                  </Flex>
                  <Box position="relative">
                    {isLoading && (
                      <Box
                        bg="gray.600"
                        height="28vh"
                        left={0}
                        position="absolute"
                        top={0}
                        width="100%"
                        zIndex={1}
                      >
                        <CardLoading />
                      </Box>
                    )}
                    <ArticleSummaryReport
                      avatar=""
                      baseNewsId={baseNewsId}
                      clearCache={clearCache}
                      company={s}
                      handleSetPagePdf={handleSetPagePdf}
                      isDetailModalOpen={isDetailModalOpen}
                      newsId={s.news_id?.toString()}
                      pdfPage={pdfPage ?? '1'}
                      question=""
                      selectedNewsId={s.news_id?.toString()}
                      onChangeInput={() => console.log()}
                      onEnter={() => console.log()}
                      onLoadingChange={handleLoadingChange}
                      onSubmit={() => console.log()}
                    />
                  </Box>
                </Box>
              )
            })}
          </MotionBox>
        </Box>
      </Box>
    </Box>
  )
}
